import "./App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { LanguageProvider } from "./i18n";
import AnnouncementBar from "./components/AnnouncementBar";
import TopBar from "./components/TopBar";
import AuthCallback from "./components/AuthCallback";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import Designer from "./pages/Designer";
import Cart from "./pages/Cart";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import Orders from "./pages/Orders";
import MyDesigns from "./pages/MyDesigns";
import Admin from "./pages/Admin";

const HOME_ROBOTS = "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1";

// Keeps exactly one <meta name="robots">: index on the homepage, noindex,follow elsewhere.
function RouteMeta() {
  const location = useLocation();
  useEffect(() => {
    const robots = document.querySelector('meta[name="robots"]');
    if (robots) {
      const p = location.pathname;
      const indexable = p === "/" || p === "/design" || p.startsWith("/stickers/");
      robots.setAttribute("content", indexable ? HOME_ROBOTS : "noindex,follow");
    }
  }, [location.pathname]);
  return null;
}

function NotFound() {
  return (
    <div className="max-w-lg mx-auto px-5 py-24 text-center" data-testid="not-found">
      <h1 className="font-head font-extrabold text-4xl mb-3">Page not found</h1>
      <p className="text-nbblack/60 mb-6">The page you're looking for doesn't exist.</p>
      <Link to="/" className="inline-flex bg-nbpink text-white font-head font-bold px-6 py-3 rounded-full nb-border nb-shadow nb-press">Back home</Link>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Handle OAuth return synchronously before any protected checks.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/design" element={<Designer />} />
      <Route path="/stickers/:finish" element={<Designer />} />
      <Route path="/cart" element={<Cart />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      <Route path="/orders" element={<Orders />} />
      <Route path="/designs" element={<MyDesigns />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <LanguageProvider>
            <AuthProvider>
              <CartProvider>
                <Toaster position="top-center" richColors />
                <RouteMeta />
                <TopBar />
                <AnnouncementBar />
                <AppRouter />
              </CartProvider>
            </AuthProvider>
          </LanguageProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
